import React, { useState, useRef, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import { ptBR } from 'date-fns/locale'
import { parseBrDate } from 'sharedKernel/handlers'
import {
  DatePickerGlobalStyles,
  Wrapper,
  TriggerButton,
  ChevronIcon,
  Popover,
  PopoverHeader,
  PopoverTitle,
  DateInputWrapper,
  DateInput,
  DateInputSeparator,
  CalendarIcon,
  CalendarWrapper,
  PopoverFooter,
  ClearButton,
  ApplyButton,
} from './index.styles'

const formatDate = (date: Date | null): string => {
  if (!date) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
}

const maskDate = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

const parseInputDate = (masked: string): Date | null => {
  if (masked.length !== 10) return null
  const parsed = parseBrDate(masked)
  return parsed ? parsed.toJSDate() : null
}

const isEndIncompleteOrInvalid = (text: string, date: Date | null): boolean => {
  if (!text) return false
  if (text.length < 10) return true
  return date == null
}

type DateRegisterFilterProps = {
  value: { start: Date | null; end: Date | null }
  onChange: (range: { start: Date | null; end: Date | null }) => void
  isArrow?: boolean
}

export default function DateRegisterFilter({ value, onChange, isArrow = true }: DateRegisterFilterProps) {
  const [open, setOpen] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(value?.start ?? null)
  const [endDate, setEndDate] = useState<Date | null>(value?.end ?? null)
  const [startText, setStartText] = useState(formatDate(value?.start ?? null))
  const [endText, setEndText] = useState(formatDate(value?.end ?? null))

  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setStartDate(value?.start ?? null)
    setEndDate(value?.end ?? null)
    setStartText(formatDate(value?.start ?? null))
    setEndText(formatDate(value?.end ?? null))
  }, [value])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const startInvalid = startText.length === 10 && startDate == null
  const endInvalid = endText.length === 10 && endDate == null
  const rangeOrderInvalid = !!(startDate && endDate && startDate > endDate)
  const canApply =
    !!startDate && !isEndIncompleteOrInvalid(endText, endDate) && !rangeOrderInvalid

  const handleTextChange = (
    raw: string,
    setText: (text: string) => void,
    setDate: (date: Date | null) => void
  ) => {
    const masked = maskDate(raw)
    setText(masked)
    setDate(masked.length === 10 ? parseInputDate(masked) : null)
  }

  const handleCalendarChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start)
    setEndDate(end)
    setStartText(formatDate(start))
    setEndText(formatDate(end))
  }

  const handleApply = () => {
    if (!canApply) return
    onChange({ start: startDate, end: endDate })
    setOpen(false)
  }

  const handleClear = () => {
    setStartDate(null)
    setEndDate(null)
    setStartText('')
    setEndText('')
    onChange({ start: null, end: null })
  }

  return (
    <Wrapper ref={ref}>
      <DatePickerGlobalStyles />

      <TriggerButton $active={!!value?.start} onClick={() => setOpen((v) => !v)}>
        <CalendarIcon>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M1.5 6h13" stroke="currentColor" strokeWidth="1.2" />
            <path d="M5 1v3M11 1v3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </CalendarIcon>
        Data de cadastro
        {isArrow && (
          <ChevronIcon $open={open}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 4L6 8L10 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ChevronIcon>
        )}
      </TriggerButton>

      {open && (
        <Popover>
          <PopoverHeader>
            <PopoverTitle>Data de cadastro</PopoverTitle>
            <DateInputWrapper>
              <DateInput
                value={startText}
                placeholder="DD/MM/AAAA"
                maxLength={10}
                inputMode="numeric"
                aria-label="Data inicial de cadastro"
                $isInvalid={startInvalid || rangeOrderInvalid}
                onChange={(e) => handleTextChange(e.target.value, setStartText, setStartDate)}
              />
              <DateInputSeparator>—</DateInputSeparator>
              <DateInput
                value={endText}
                placeholder="DD/MM/AAAA"
                maxLength={10}
                inputMode="numeric"
                aria-label="Data final de cadastro"
                $isInvalid={endInvalid || rangeOrderInvalid}
                onChange={(e) => handleTextChange(e.target.value, setEndText, setEndDate)}
              />
            </DateInputWrapper>
          </PopoverHeader>

          <CalendarWrapper>
            <DatePicker
              locale={ptBR}
              selected={startDate}
              onChange={(dates) => handleCalendarChange(dates as [Date | null, Date | null])}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              inline
              calendarStartDay={0}
            />
          </CalendarWrapper>

          <PopoverFooter>
            <ClearButton onClick={handleClear}>Limpar</ClearButton>
            <ApplyButton onClick={handleApply} disabled={!canApply}>
              Aplicar
            </ApplyButton>
          </PopoverFooter>
        </Popover>
      )}
    </Wrapper>
  )
}
